import { asyncToGenerator as _asyncToGenerator, extends as _extends, objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose } from './_virtual/_rollupPluginBabelHelpers.js';
import { createElement } from 'react';
import { matchPath, StaticRouter } from 'react-router-dom';
import _regeneratorRuntime from 'regenerator-runtime';
import { getAllRoutes } from './utils.esm.js';
import { loadInitialProps } from './loadInitialProps.esm.js';
import { After } from './After.esm.js';
import { __AfterContext, Document } from './Document.esm.js';
import { getAssets } from './getAssets.esm.js';
import { renderToStaticMarkup, renderToString } from 'react-dom/server';
import { Helmet } from 'react-helmet';
import { parse } from 'url';

var _excluded = ["req", "res", "routes", "assets", "document", "customRenderer", "chunks", "scrollToTop", "ssg"],
    _excluded2 = ["html"];

var modPageFn = function modPageFn(Page) {
  return function RenderAfter(props) {
    return createElement(Page, Object.assign({}, props));
  };
};
/**
 * The customRenderer parameter is a (potentially async) function that can be set to return
 * more than just a rendered string.
 * If present, it will be used instead of the default ReactDOMServer renderToString function.
 * It has to return an object of shape { html, ... }, in which html will be used as the rendered string
 * Other props will be also pass to the Document component
 */


function renderApp(_x) {
  return _renderApp.apply(this, arguments);
}

function _renderApp() {
  _renderApp = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(options) {
    var req, res, pureRoutes, assets, Document$1, customRenderer, chunks, _options$scrollToTop, scrollToTop, _options$ssg, ssg, rest, autoScrollRef, routes, ctx, pathname, _yield$loadInitialPro, match, initialData, context, redirectTo, statusCode, afterData, data, renderPage, Doc, _getAssets, scripts, styles, reactRouterMatch, _yield$Doc$getInitial, html, docProps, props, doc, page;

    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            req = options.req, res = options.res, pureRoutes = options.routes, assets = options.assets, Document$1 = options.document, customRenderer = options.customRenderer, chunks = options.chunks, _options$scrollToTop = options.scrollToTop, scrollToTop = _options$scrollToTop === void 0 ? true : _options$scrollToTop, _options$ssg = options.ssg, ssg = _options$ssg === void 0 ? false : _options$ssg, rest = _objectWithoutPropertiesLoose(options, _excluded);
            autoScrollRef = {
              current: scrollToTop
            }; // check and see if 404 page is in list of app routes
            // if there wan no 404 page in routes array add After.js default 404 page

            routes = getAllRoutes(pureRoutes);
            ctx = _extends({
              req: req,
              res: res,
              scrollToTop: autoScrollRef
            }, rest);
            pathname = parse(req.url).pathname; // finds related component for the current path (request url)
            //  and calls component.getInitialProps({ match,...ctx })

            _context2.next = 7;
            return loadInitialProps(pathname, routes, ctx);

          case 7:
            _yield$loadInitialPro = _context2.sent;
            match = _yield$loadInitialPro.match;
            initialData = _yield$loadInitialPro.data;
            // <StaticRouter /> context object, which get mutated by React Router
            // and it contains information about statusCode and target <Redirect /> component target url (if any)
            // https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/StaticRouter.md#context-object
            context = {}; // here we will check result of the getInitialProps
            // and see if it contains redirectTo or statusCode properties
            // we will mutate context if we got redirectTo or statusCode in initialData

            if (!initialData) {
              _context2.next = 16;
              break;
            }

            redirectTo = initialData.redirectTo, statusCode = initialData.statusCode;

            if (statusCode) {
              context.statusCode = statusCode;
            } // if we got redirectTo from getInitalProps
            // we don't waste server resources by rendering the react tree
            // so we return early


            if (!redirectTo) {
              _context2.next = 16;
              break;
            }

            return _context2.abrupt("return", {
              html: '',
              data: initialData,
              redirect: redirectTo,
              statusCode: statusCode || 302
            });

          case 16:
            // this object containes data that <After /> will use on client-side
            afterData = {
              scrollToTop: autoScrollRef
            }; // well, we will save a few bits in this way

            if (ssg) {
              afterData.ssg = true;
            } // result of getInitialProps + <After /> props


            data = {
              initialData: initialData,
              afterData: afterData
            };

            renderPage = /*#__PURE__*/function () {
              var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(fn) {
                var defaultRenderer, renderer, asyncOrSyncRender, renderedContent, helmet;
                return _regeneratorRuntime.wrap(function _callee$(_context) {
                  while (1) {
                    switch (_context.prev = _context.next) {
                      case 0:
                        if (fn === void 0) {
                          fn = modPageFn;
                        }

                        // By default, we keep ReactDOMServer synchronous renderToString function
                        defaultRenderer = function defaultRenderer(element) {
                          return {
                            html: renderToString(element)
                          };
                        };

                        renderer = customRenderer || defaultRenderer;
                        asyncOrSyncRender = renderer(createElement(StaticRouter, {
                          location: req.url,
                          context: context
                        }, fn(After)({
                          routes: routes,
                          data: data,
                          transitionBehavior: 'blocking'
                        })));
                        _context.next = 6;
                        return asyncOrSyncRender;

                      case 6:
                        renderedContent = _context.sent;
                        helmet = Helmet.renderStatic();
                        return _context.abrupt("return", _extends({
                          helmet: helmet
                        }, renderedContent));

                      case 9:
                      case "end":
                        return _context.stop();
                    }
                  }
                }, _callee);
              }));

              return function renderPage(_x2) {
                return _ref.apply(this, arguments);
              };
            }();

            Doc = Document$1 || Document; // get css and javascript file paths for the async componetns

            _getAssets = getAssets({
              route: match,
              chunks: chunks
            }), scripts = _getAssets.scripts, styles = _getAssets.styles;
            reactRouterMatch = matchPath(req.url, match); // Docuement.getInitialProps() will call renderPage()
            // and renderPage() will call ReactDOMServer.renderToString
            // so we get our React Tree html from this function

            _context2.next = 25;
            return Doc.getInitialProps(_extends({
              req: req,
              res: res,
              assets: assets,
              renderPage: renderPage,
              data: data,
              helmet: Helmet.renderStatic(),
              match: reactRouterMatch,
              scripts: scripts,
              styles: styles,
              scrollToTop: autoScrollRef
            }, rest));

          case 25:
            _yield$Doc$getInitial = _context2.sent;
            html = _yield$Doc$getInitial.html;
            docProps = _objectWithoutPropertiesLoose(_yield$Doc$getInitial, _excluded2);

            if (!context.url) {
              _context2.next = 30;
              break;
            }

            return _context2.abrupt("return", {
              html: '',
              data: initialData,
              redirect: context.url,
              statusCode: context.statusCode || 302
            });

          case 30:
            props = _extends({
              assets: assets,
              data: data,
              scripts: scripts,
              styles: styles,
              match: reactRouterMatch
            }, rest, docProps, {
              html: html
            }); // we render <Docuemnt /> which is our app shell

            doc = renderToStaticMarkup(createElement(__AfterContext.Provider, {
              value: props
            }, createElement(Doc, Object.assign({}, props))));
            page = "<!doctype html>" + doc;
            return _context2.abrupt("return", {
              html: page,
              data: initialData,
              redirect: '',
              statusCode: context.statusCode || 200
            });

          case 34:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _renderApp.apply(this, arguments);
}

export { renderApp };
//# sourceMappingURL=renderApp.esm.js.map
